import * as _ from 'lodash';
import moment from 'moment';

import { isNullOrEmpty } from '../../utilities/stringUtil';
import PrintUtility from '../../utilities/printUtility';
import {
  RECORDING_METHOD,
  SORT_TYPE,
  RADIO_BUTTON,
  DATE_TIME_FORMAT,
  ENTRANCE_WORKER_TYPES,
  ENTRANCE_EXIT_RECORD_STATUS,
  SEARCH_CCUS_SENT_STATUS,
  SEARCH_CCUS_SENT_STATUS_CONDENSED,
} from '../../utilities/constants';

/**
 * 入退場実績一覧検索パラメータ作成
 * @param {object}param:検索パラメーター
 * @return {object}searchParam:API通信用検索パラメーター
 * */
const createSearchInOutResult = (param, spotFlg) => {
  const searchParam = Object.create(null);
  searchParam.offset = param.offset;
  searchParam.from_date = moment(param.fromDate).format(
    DATE_TIME_FORMAT.slashDateFormat
  );
  searchParam.to_date = moment(param.toDate).format(
    DATE_TIME_FORMAT.slashDateFormat
  );
  searchParam.prior_ccus_error_flg = param.priorCcusError ? 1 : 0;
  if (!isNullOrEmpty(param.selectedApprovedStatus)) {
    searchParam.approved_flg = param.selectedApprovedStatus;
  }
  if (!isNullOrEmpty(param.selectedSentStatus)) {
    // BLDE_V2-50500 【B-441の残対応】入退場実績画面の送信状態に関する検索条件のUI変更
    // 送信状態に関する検索条件の表示項目を減らし、各項目に複数の送信状態を含めるように改善
    let selectedSentStatuses = [];
    switch (Number(param.selectedSentStatus)) {
      case SEARCH_CCUS_SENT_STATUS_CONDENSED.UNSENT.ID:
        // 未送信：変更未送信、送信中、変更送信中を含む
        selectedSentStatuses = [
          SEARCH_CCUS_SENT_STATUS.unsent.id,
          SEARCH_CCUS_SENT_STATUS.notSentAfterChange.id,
          SEARCH_CCUS_SENT_STATUS.sending.id,
          SEARCH_CCUS_SENT_STATUS.sendingAfterChange.id,
        ];
        break;
      case SEARCH_CCUS_SENT_STATUS_CONDENSED.UNABLE_TO_SEND.ID:
        // 送信不可：対象外、変更送信不可を含む
        selectedSentStatuses = [
          SEARCH_CCUS_SENT_STATUS.unableToSend.id,
          SEARCH_CCUS_SENT_STATUS.exempt.id,
          SEARCH_CCUS_SENT_STATUS.unableToSendAfterChange.id,
        ];
        break;
      case SEARCH_CCUS_SENT_STATUS_CONDENSED.SENT.ID:
        // 送信済
        selectedSentStatuses = [SEARCH_CCUS_SENT_STATUS.sent.id];
        break;
      case SEARCH_CCUS_SENT_STATUS_CONDENSED.SENDING_ERROR.ID:
        // 送信エラー
        selectedSentStatuses = [SEARCH_CCUS_SENT_STATUS.sendingError.id];
        break;
      default:
        break;
    }
    searchParam.ccus_send_status = selectedSentStatuses;
  }
  if (!isNullOrEmpty(param.workerName)) {
    searchParam.worker_name = param.workerName;
  }
  if (!isNullOrEmpty(param.fieldTreeIds) && param.fieldTreeIds.length > 0) {
    searchParam.field_tree_ids = param.fieldTreeIds;
  }
  if (
    param.bodyTemperatureEnabled === true &&
    !isNullOrEmpty(param.bodyTemperature)
  ) {
    searchParam.body_temperature = param.bodyTemperature;
  }
  if (!isNullOrEmpty(param.order)) {
    if (param.sortType === SORT_TYPE.asc.id) {
      searchParam.order = param.order + SORT_TYPE.asc.name;
    } else {
      searchParam.order = param.order + SORT_TYPE.desc.name;
    }
  }
  if (
    !isNullOrEmpty(param.workerTypesList) &&
    param.workerTypesList.length > 0 &&
    param.workerTypesList.length !==
      Object.keys(ENTRANCE_WORKER_TYPES).length &&
    spotFlg
  ) {
    searchParam.worker_types = param.workerTypesList.map(
      entranceWorkersStatus => entranceWorkersStatus.id
    );
  } else if (!spotFlg) {
    searchParam.worker_types = [ENTRANCE_WORKER_TYPES.worker.id];
  }
  if (!_.isEmpty(param.primaryCompanyIds)) {
    searchParam.primary_company_ids = param.primaryCompanyIds;
  }
  if (
    !_.isEmpty(param.entranceExitRecordStatus) &&
    param.entranceExitRecordStatus.length !==
      Object.keys(ENTRANCE_EXIT_RECORD_STATUS).length
  ) {
    searchParam.entrance_exit_record_statuses = _.map(
      param.entranceExitRecordStatus,
      item => item.id
    );
  }
  // BLDE_V2-42611  CCUS事業者ID削除フラグ用の検索
  if (!isNullOrEmpty(param.ccusJigyousyaDeleted)) {
    searchParam.ccus_jigyousya_deleted_flag = param.ccusJigyousyaDeleted;
  }
  return searchParam;
};

/**
 * 入退場実績登録パラメータ作成
 * @param {object}param:登録パラメーター
 * @return {object}createParam:API通信用登録パラメーター
 * */
const createRegisterInOutResult = param => {
  const createParam = Object.create(null);
  createParam.field_tree_worker_id = param.fieldTreeWorkerId;
  createParam.entrance_worker_id = param.workerId;
  createParam.target_date = param.targetDate;
  createParam.in_hour = zeroPadding(Number(param.inHour));
  createParam.in_min = zeroPadding(Number(param.inMin));
  createParam.remarks = param.remarks;
  if (!isNullOrEmpty(param.manualBodyTemperature)) {
    createParam.manual_body_temperature = param.manualBodyTemperature;
  }
  if (param.recordingMethod === RECORDING_METHOD.entranceAndExit.id) {
    if (!isNullOrEmpty(param.outHour)) {
      createParam.out_hour = zeroPadding(Number(param.outHour));
    }
    if (!isNullOrEmpty(param.outMin)) {
      createParam.out_min = zeroPadding(Number(param.outMin));
    }
    if (!isNullOrEmpty(param.nextFlg)) {
      createParam.next_flg = param.nextFlg;
    }
  }
  return createParam;
};

/**
 * 入退場実績更新パラメータ作成
 * @param {object}param:更新パラメーター
 * @return {object}updateParam:API通信用更新パラメーター
 * */
const createUpdateInOutResult = param => {
  const updateParam = Object.create(null);
  updateParam.field_tree_worker_id = param.fieldTreeWorkerId;
  updateParam.entrance_worker_id = param.workerId;
  updateParam.recording_method = param.recordingMethod;
  updateParam.target_date = param.targetDate;
  updateParam.in_hour = zeroPadding(Number(param.inHour));
  updateParam.in_min = zeroPadding(Number(param.inMin));
  updateParam.manual_body_temperature = param.manualBodyTemperature;
  updateParam.remarks = param.remarks;

  if (param.recordingMethod === RECORDING_METHOD.entranceAndExit.id) {
    if (!isNullOrEmpty(param.outHour)) {
      updateParam.out_hour = zeroPadding(Number(param.outHour));
    }
    if (!isNullOrEmpty(param.outMin)) {
      updateParam.out_min = zeroPadding(Number(param.outMin));
    }
    if (!isNullOrEmpty(param.nextFlg)) {
      updateParam.next_flg = param.nextFlg;
    }
  }
  return updateParam;
};

/**
 * 入退場実績削除パラメータ作成
 * @param {object}param:削除パラメーター
 * @return {object}deleteParam:API通信用削除パラメーター
 * */
const createDeleteList = param => {
  const deleteParam = Object.create(null);
  deleteParam.check_in_out_log_ids = checkInOutLogListFlatMap(param);
  return deleteParam;
};

/**
 * 入退場実績承認パラメータ作成
 * @param {object}param:承認パラメーター
 * @return {object}approveParam:API通信用承認パラメーター
 * */
const createApproveList = param => {
  const approveParam = Object.create(null);
  approveParam.check_in_out_log_ids = checkInOutLogListFlatMap(param);
  return approveParam;
};

/**
 * 入退場実績承認解除パラメータ作成
 * @param {object}param:承認解除パラメーター
 * @return {object}unapprovedParam:API通信用承認解除パラメーター
 * */
const createUnapprovedList = param => {
  const unapprovedParam = Object.create(null);
  unapprovedParam.check_in_out_log_ids = checkInOutLogListFlatMap(param);
  return unapprovedParam;
};

/**
 * 入退場実績更新用のフォームデータを作成する
 * @param {object}inOutResult:editForm作成元となる入退場実績データ
 * @param {object}fieldSettings:現場設定
 * @return {object}editForm
 * */
const createEditForm = (inOutResult, fieldSettings) => {
  const editForm = Object.create(null);
  editForm.checkInOutLogId = inOutResult.id;
  editForm.fieldTreeWorkerId = inOutResult.field_tree_worker_id;
  editForm.workerId = inOutResult.entrance_worker_id;
  editForm.workerName = inOutResult.worker_name;
  editForm.workerType = inOutResult.worker_type;
  editForm.spotCompanyName = inOutResult.spot_company_name;
  editForm.primeCompanyName = inOutResult.company_name;
  editForm.recordingMethod = '';
  editForm.targetDate = inOutResult.target_date;
  editForm.inHour = Number(inOutResult.in_hour);
  editForm.inMin = Number(inOutResult.in_min);
  editForm.outHour = Number(inOutResult.out_hour);
  editForm.outMin = Number(inOutResult.out_min);
  editForm.nextFlg = inOutResult.next_flg;
  editForm.categoryMajorCode = inOutResult.job_category_major_code;
  editForm.categoryMajorName = inOutResult.job_category_major_name;
  editForm.categoryMinorCode = inOutResult.job_category_minor_code;
  editForm.categoryMinorName = inOutResult.job_category_minor_name;
  editForm.positionCode = inOutResult.position_code;
  editForm.positionName = inOutResult.position_name;
  editForm.workContent = inOutResult.work_content;
  editForm.blockedOn = inOutResult.blocked_on;
  editForm.bodyTemperature = inOutResult.body_temperature;
  editForm.manualBodyTemperature = inOutResult.manual_body_temperature;
  editForm.remarks = inOutResult.remarks;
  editForm.manualUpdateUser = inOutResult.manual_update_user_name;
  editForm.manualUpdateAt = inOutResult.manual_update_at;

  // 退場データがないかつ、現場設定で入場のみの場合は退場は出さない
  const enableOutHour = !isNullOrEmpty(inOutResult.out_hour);
  if (
    enableOutHour ||
    fieldSettings.recording_method === RECORDING_METHOD.entranceAndExit.id
  ) {
    if (enableOutHour) {
      editForm.recordingMethod = RECORDING_METHOD.entranceAndExit.id;
    } else {
      editForm.recordingMethod = RECORDING_METHOD.onlyAdmission.id;
    }
  } else {
    editForm.recordingMethod = '';
  }
  return editForm;
};

/**
 * 入場日と退場日の比較
 * @param {object}param:追加・更新用プロパティ
 * @return {boolean}false:入退場フラグがOFFで入場日が退場日より後の時刻が設定されている
 * */
const isOutTimeSameOrLaterThanInTime = param => {
  if (
    param.nextFlg === RADIO_BUTTON.On ||
    param.recordingMethod !== RECORDING_METHOD.entranceAndExit.id
  ) {
    return true;
  }

  const inDateTime = moment(
    new Date(
      data.date.year,
      data.date.month,
      data.date.day,
      Number(param.inHour),
      Number(param.inMin),
      data.date.sec
    ).toISOString()
  );
  const outDateTime = moment(
    new Date(
      data.date.year,
      data.date.month,
      data.date.day,
      Number(param.outHour),
      Number(param.outMin),
      data.date.sec
    ).toISOString()
  );
  return outDateTime.isSameOrAfter(inDateTime);
};

/**
 * 検索フォームの内容が合致するかどうかを返却する
 * @param {object} searchFormA
 * @param {object} searchFormB
 * @return {boolean} true:同一、false:異なっている
 * */
const isSameSearchForm = (searchFormA, searchFormB) => {
  return !(
    searchFormA.toDate !== searchFormB.toDate ||
    searchFormA.fromDate !== searchFormB.fromDate ||
    searchFormA.priorCcusError !== searchFormB.priorCcusError ||
    searchFormA.order !== searchFormB.order ||
    searchFormA.sortType !== searchFormB.sortType
  );
};

const checkInOutLogListFlatMap = param => {
  return _.flatMap(param, getParamId);
};

const getParamId = param => {
  return param.id;
};

const zeroPadding = number => {
  return `0${number}`.slice(-2);
};

const downloadCsv = (blob, fieldId) => {
  const csvBlob = new Blob([blob], { type: 'text/csv;charset=shift-jis;' });
  const filename = `入退場実績_${fieldId}_${moment(new Date()).format(
    DATE_TIME_FORMAT.dateTimeSecondFormat
  )}.csv`;
  PrintUtility.printBlob(csvBlob, filename);
};

/**
 * ダッシュボードで利用するビジネスロジック格納クラス
 * */
const InOutResultService = {
  createSearchInOutResult,
  createRegisterInOutResult,
  createUpdateInOutResult,
  createDeleteList,
  createApproveList,
  createUnapprovedList,
  createEditForm,
  isOutTimeSameOrLaterThanInTime,
  isSameSearchForm,
  downloadCsv,
};

export default InOutResultService;

// 定数
const data = {
  date: {
    year: 0,
    month: 0,
    day: 0,
    sec: 0,
  },
};



// WEBPACK FOOTER //
// ./src/services/inOutResult/InOutResultService.js